import { useState, useMemo, useRef, useEffect } from 'react';
import type { EngagementSnapshot } from '@/types/emotion';
import { clsx } from 'clsx';

type Props = {
  snapshots: EngagementSnapshot[];
};

// ⚡ PERFORMANCE: Limit history to last 50 blocks (approx 2 mins of history)
const MAX_HISTORY = 50;
const INACTIVE_THRESHOLD_MS = 10000; // Gray out if no data for 10s

// 🎨 COLORS: Mapped to your specific palette
const getColor = (value: number) => {
  if (value > 0.7) return 'bg-green-500 shadow-[0_0_8px_-2px_rgba(34,197,94,0.6)]'; // High Engagement
  if (value > 0.5) return 'bg-yellow-500/80';
  if (value > 0.3) return 'bg-orange-500/80';
  return 'bg-red-500/60'; // Disengaged
};

export const EngagementHeatmap = ({ snapshots }: Props) => {
  const [mode, setMode] = useState<'students' | 'pulse'>('students');
  const scrollRef = useRef<HTMLDivElement>(null);

  // 1. DATA PROCESSING
  const { studentRows, classPulse } = useMemo(() => {
    // A. Group by Student
    const buckets = snapshots.reduce<Record<string, EngagementSnapshot[]>>((acc, snapshot) => {
      acc[snapshot.studentId] = [...(acc[snapshot.studentId] ?? []), snapshot];
      return acc;
    }, {});

    // B. Process Student Rows (Sort, Slice, Check Activity)
    const rows = Object.keys(buckets).map(studentId => {
      const history = buckets[studentId].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const lastPoint = history[history.length - 1];
      const isInactive = Date.now() - new Date(lastPoint.timestamp).getTime() > INACTIVE_THRESHOLD_MS;
      
      return {
        id: studentId,
        name: lastPoint.studentName || studentId,
        history: history.slice(-MAX_HISTORY), // <--- SLIDING WINDOW (Last 50)
        isInactive,
        average: history.reduce((sum, h) => sum + h.engaged, 0) / history.length
      };
    });

    // C. Process Class Pulse (Aggregate by Timestamp)
    const timeMap: Record<string, { total: number; count: number; timestamp: string }> = {};
    snapshots.forEach(s => {
      // Group by 2-second windows to align ticks
      const timeKey = Math.floor(new Date(s.timestamp).getTime() / 2000) * 2000; 
      if (!timeMap[timeKey]) timeMap[timeKey] = { total: 0, count: 0, timestamp: s.timestamp };
      timeMap[timeKey].total += s.engaged;
      timeMap[timeKey].count += 1;
    });

    const pulse = Object.values(timeMap)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .map(t => ({
        timestamp: t.timestamp,
        engaged: t.total / t.count,
        studentName: 'Class Average' 
      }))
      .slice(-MAX_HISTORY); // <--- SLIDING WINDOW (Last 50)

    return { studentRows: rows, classPulse: pulse };
  }, [snapshots]);

  // 2. AUTO-SCROLL: Keep lists scrolled to the right
  useEffect(() => {
    // This is a rough "scroll all" hack. In production, individual refs are better, 
    // but for 50 blocks, this selector is fast enough.
    const scrollers = document.querySelectorAll('.heatmap-scroll-area');
    scrollers.forEach(el => {
      el.scrollLeft = el.scrollWidth;
    });
  }, [snapshots, mode]);

  return (
    <div className="rounded-2xl border border-background-border bg-background-surface p-6 flex flex-col h-full min-h-[300px]">
      
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm font-semibold text-text-primary">Attention Heatmap</p>
          <p className="text-xs text-text-dimmed mt-1">Real-time engagement blocks (Last {MAX_HISTORY})</p>
        </div>
        
        {/* TAB SWITCHER */}
        <div className="flex bg-background-main p-1 rounded-lg border border-background-border">
          <button
            onClick={() => setMode('students')}
            className={clsx("px-3 py-1 text-xs font-medium rounded-md transition-all", mode === 'students' ? "bg-background-border text-text-primary" : "text-text-dimmed hover:text-text-primary")}
          >
            👥 Students
          </button>
          <button
            onClick={() => setMode('pulse')}
            className={clsx("px-3 py-1 text-xs font-medium rounded-md transition-all", mode === 'pulse' ? "bg-brand/30 text-text-primary" : "text-text-dimmed hover:text-text-primary")}
          >
            ❤️ Pulse
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar">
        
        {/* MODE A: STUDENT LIST */}
        {mode === 'students' && studentRows.map((student) => (
          <div key={student.id} className={clsx("transition-opacity duration-500", student.isInactive && "opacity-40 grayscale")}>
            {/* Student Header */}
            <div className="flex justify-between items-end mb-1 px-1">
              <span className="text-xs font-bold text-text-primary flex items-center gap-2">
                {student.isInactive ? '💤' : '🟢'} {student.name}
              </span>
              <span className="text-[10px] text-text-dimmed font-mono">
                AVG: {Math.round(student.average * 100)}%
              </span>
            </div>

            {/* Horizontal Ticker Tape */}
            <div className="heatmap-scroll-area flex gap-[2px] overflow-x-auto pb-2 scroll-smooth no-scrollbar">
              {student.history.map((block) => (
                <div
                  key={block.timestamp}
                  className={clsx("h-8 w-3 min-w-[12px] rounded-sm transition-all hover:scale-125 hover:z-10 cursor-help", getColor(block.engaged))}
                  title={`${new Date(block.timestamp).toLocaleTimeString()} • ${Math.round(block.engaged * 100)}%`}
                />
              ))}
              {/* Spacer to allow scrolling to the very end */}
              <div className="min-w-[1px] h-8" />
            </div>
          </div>
        ))}

        {/* MODE B: CLASS PULSE */}
        {mode === 'pulse' && (
          <div className="mt-8">
            <div className="flex justify-between items-end mb-2 px-1">
               <span className="text-sm font-bold text-text-primary">Class Average Pulse</span>
            </div>
            <div className="heatmap-scroll-area flex gap-1 overflow-x-auto pb-4 scroll-smooth">
              {classPulse.map((block) => (
                <div
                  key={block.timestamp}
                  className={clsx("h-16 w-5 min-w-[20px] rounded hover:scale-110 transition-all", getColor(block.engaged))}
                  title={`Class Average • ${new Date(block.timestamp).toLocaleTimeString()} • ${Math.round(block.engaged * 100)}%`}
                />
              ))}
            </div>
            <p className="text-xs text-text-dimmed text-center mt-4">
              This view aggregates all active students into a single rhythm to show the "vibe" of the room.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};